import React, { useState, useEffect } from 'react';
import { Card, Statistic, Icon, Loader, Message, Button, Label, Table, Confirm, Segment } from 'semantic-ui-react';
import axios from 'axios';

const DRIP_STEPS = [
    { step: 0, label: 'Not Started', icon: 'hourglass start' },
    { step: 1, label: 'Welcome (15min)', icon: 'mail' },
    { step: 2, label: 'Day 1', icon: 'calendar' },
    { step: 3, label: 'Day 3', icon: 'calendar' },
    { step: 4, label: 'Day 7', icon: 'calendar' },
    { step: 5, label: 'Day 10', icon: 'calendar' },
    { step: 6, label: 'Day 14 (Complete)', icon: 'check circle' },
];

const DRIP_STEP_LABELS = ['Welcome', 'Day 1', 'Day 3', 'Day 7', 'Day 10', 'Day 14'];

const EmailOverview = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStep, setSelectedStep] = useState(null);
    const [toggling, setToggling] = useState(false);
    const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);

    const fetchStatus = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/admin/emails/status');
            setStatus(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchStatus(); }, []);

    const handleToggle = async () => {
        setToggling(true);
        try {
            const newState = !status.effectivelyEnabled;
            await axios.post('/admin/emails/toggle', { enabled: newState });
            setStatus(prev => ({ ...prev, effectivelyEnabled: newState }));
            setToggleConfirmOpen(false);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setToggling(false);
        }
    };

    if (loading) return <Loader active inline="centered" content="Loading email status..." />;
    if (error) return <Message negative header="Error loading status" content={error} />;
    if (!status) return null;

    const sectionHeaderStyle = { color: '#2c3e50', marginBottom: '16px', marginTop: '32px', display: 'flex', alignItems: 'center', gap: '8px' };
    const cardStyle = { padding: '20px', borderRadius: '8px' };

    return (
        <div>
            {/* Stats Cards Row 1 */}
            <Card.Group itemsPerRow={4} style={{ marginBottom: '24px' }}>
                <Card style={cardStyle}>
                    <Card.Content>
                        <Statistic size="small">
                            <Statistic.Value style={{ color: '#0884a8' }}>{status.totalUsers}</Statistic.Value>
                            <Statistic.Label>Total Active Users</Statistic.Label>
                        </Statistic>
                    </Card.Content>
                </Card>
                <Card style={cardStyle}>
                    <Card.Content>
                        <Statistic size="small">
                            <Statistic.Value style={{ color: '#28a745' }}>{status.subscribedCount}</Statistic.Value>
                            <Statistic.Label>Subscribed</Statistic.Label>
                        </Statistic>
                    </Card.Content>
                </Card>
                <Card style={cardStyle}>
                    <Card.Content>
                        <Statistic size="small">
                            <Statistic.Value style={{ color: '#dc3545' }}>{status.unsubscribedCount}</Statistic.Value>
                            <Statistic.Label>Unsubscribed ({status.unsubscribeRate}%)</Statistic.Label>
                        </Statistic>
                    </Card.Content>
                </Card>
                <Card style={cardStyle}>
                    <Card.Content>
                        <Statistic size="small">
                            <Statistic.Value>
                                <Icon name={status.effectivelyEnabled ? 'check circle' : 'times circle'}
                                    color={status.effectivelyEnabled ? 'green' : 'red'} />
                            </Statistic.Value>
                            <Statistic.Label>
                                Email Jobs {status.effectivelyEnabled ? 'Enabled' : 'Disabled'}
                            </Statistic.Label>
                        </Statistic>
                        <div style={{ textAlign: 'center', marginTop: '8px' }}>
                            {status.emailJobsEnabled ? (
                                <Button
                                    size="tiny"
                                    color={status.effectivelyEnabled ? 'red' : 'green'}
                                    basic
                                    onClick={() => setToggleConfirmOpen(true)}
                                    loading={toggling}
                                >
                                    <Icon name="power" /> {status.effectivelyEnabled ? 'Disable' : 'Enable'}
                                </Button>
                            ) : (
                                <Label size="tiny" color="grey">
                                    Requires deploy to enable
                                </Label>
                            )}
                        </div>
                    </Card.Content>
                </Card>
            </Card.Group>

            {/* Last Run Times + Reminder Effectiveness */}
            <Card.Group itemsPerRow={3} style={{ marginBottom: '24px' }}>
                <Card style={cardStyle}>
                    <Card.Content>
                        <Card.Header style={{ color: '#2c3e50', marginBottom: '8px' }}>
                            <Icon name="clock" style={{ color: '#0884a8' }} /> Last Drip Email
                        </Card.Header>
                        <Card.Description style={{ fontSize: '16px', color: '#445566' }}>
                            {status.lastDripSent ? new Date(status.lastDripSent).toLocaleString() : 'Never'}
                        </Card.Description>
                    </Card.Content>
                </Card>
                <Card style={cardStyle}>
                    <Card.Content>
                        <Card.Header style={{ color: '#2c3e50', marginBottom: '8px' }}>
                            <Icon name="clock" style={{ color: '#0884a8' }} /> Last Weekly Reminder
                        </Card.Header>
                        <Card.Description style={{ fontSize: '16px', color: '#445566' }}>
                            {status.lastWeeklySent ? new Date(status.lastWeeklySent).toLocaleString() : 'Never'}
                        </Card.Description>
                        {status.remindersSentThisWeek > 0 && (
                            <Label size="small" color="blue" style={{ marginTop: '8px' }}>
                                {status.remindersSentThisWeek} sent this week
                            </Label>
                        )}
                    </Card.Content>
                </Card>
                <Card style={cardStyle}>
                    <Card.Content>
                        <Card.Header style={{ color: '#2c3e50', marginBottom: '8px' }}>
                            <Icon name="line graph" style={{ color: '#28a745' }} /> Reminder Effectiveness
                        </Card.Header>
                        <Card.Description>
                            <span style={{ fontSize: '28px', fontWeight: 700, color: status.reminderEffectiveness?.rate > 50 ? '#28a745' : '#f39c12' }}>
                                {status.reminderEffectiveness?.rate || 0}%
                            </span>
                            <div style={{ fontSize: '12px', color: '#445566', marginTop: '4px' }}>
                                {status.reminderEffectiveness?.practicedAfter || 0} of {status.reminderEffectiveness?.remindedTotal || 0} reminded users
                                practiced within 7 days
                            </div>
                        </Card.Description>
                    </Card.Content>
                </Card>
            </Card.Group>

            {/* Drip Pipeline Funnel with Conversion Rates */}
            <h3 style={sectionHeaderStyle}>
                <Icon name="filter" style={{ color: '#0884a8' }} /> Drip Campaign Pipeline
                <Label size="small" style={{ marginLeft: '8px' }}>
                    {status.dripCompletionRate}% completion rate
                </Label>
            </h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {DRIP_STEPS.map(({ step, label, icon }, index) => {
                    const count = status.dripStepCounts?.[step] || 0;
                    const isSelected = selectedStep === step;
                    const conversion = status.dripConversion?.find(c => c.toStep === step);
                    return (
                        <React.Fragment key={step}>
                            {index > 0 && conversion && (
                                <div style={{ textAlign: 'center', minWidth: '40px' }}>
                                    <Icon name="arrow right" style={{ color: '#aaa' }} />
                                    <div style={{ fontSize: '11px', color: conversion.rate >= 80 ? '#28a745' : conversion.rate >= 50 ? '#f39c12' : '#dc3545', fontWeight: 600 }}>
                                        {conversion.rate}%
                                    </div>
                                </div>
                            )}
                            <Card
                                onClick={() => setSelectedStep(isSelected ? null : step)}
                                style={{
                                    ...cardStyle,
                                    minWidth: '120px',
                                    flex: 1,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    border: isSelected ? '2px solid #0884a8' : undefined,
                                    margin: 0,
                                }}
                            >
                                <Card.Content style={{ padding: '12px' }}>
                                    <Icon name={icon} size="large" style={{ color: '#0884a8', marginBottom: '4px' }} />
                                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#2c3e50' }}>{count}</div>
                                    <div style={{ fontSize: '11px', color: '#445566', marginTop: '2px' }}>
                                        {label}
                                    </div>
                                </Card.Content>
                            </Card>
                        </React.Fragment>
                    );
                })}
            </div>

            {selectedStep !== null && (() => {
                const stepInfo = DRIP_STEPS.find(s => s.step === selectedStep);
                const users = status.dripStepUsers?.[selectedStep] || [];
                return (
                    <div style={{ marginTop: '12px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                        <h4 style={{ color: '#2c3e50', marginTop: 0 }}>
                            <Icon name={stepInfo.icon} style={{ color: '#0884a8' }} />
                            Step {selectedStep}: {stepInfo.label} ({users.length})
                        </h4>
                        {users.length === 0 ? (
                            <Message info content="No users at this step" />
                        ) : (
                            <Table basic="very" compact>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Name</Table.HeaderCell>
                                        <Table.HeaderCell>Email</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {users.map((user, i) => (
                                        <Table.Row key={i}>
                                            <Table.Cell>{user.firstName} {user.lastName}</Table.Cell>
                                            <Table.Cell>{user.email}</Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        )}
                    </div>
                );
            })()}

            {/* Active Drip Users */}
            {status.activeDripUsers?.length > 0 && (
                <>
                    <h3 style={sectionHeaderStyle}>
                        <Icon name="users" style={{ color: '#0884a8' }} /> Active Drip Users
                        <Label size="small" circular color="blue">{status.activeDripUsers.length}</Label>
                    </h3>
                    <Table compact celled striped>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Name</Table.HeaderCell>
                                <Table.HeaderCell>Email</Table.HeaderCell>
                                <Table.HeaderCell>Current Step</Table.HeaderCell>
                                <Table.HeaderCell>Last Drip Sent</Table.HeaderCell>
                                <Table.HeaderCell>Next Email</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {status.activeDripUsers.map((user, i) => {
                                const nextDate = user.nextEmailDate ? new Date(user.nextEmailDate) : null;
                                const isOverdue = nextDate && nextDate < new Date();
                                return (
                                    <Table.Row key={i}>
                                        <Table.Cell>{user.firstName} {user.lastName}</Table.Cell>
                                        <Table.Cell>{user.email}</Table.Cell>
                                        <Table.Cell>
                                            <Label size="tiny" color="blue">
                                                Step {user.currentStep}: {DRIP_STEP_LABELS[user.currentStep - 1] || ''}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell style={{ color: '#445566', fontSize: '13px' }}>
                                            {user.lastDripSentAt ? new Date(user.lastDripSentAt).toLocaleDateString() : 'N/A'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {nextDate ? (
                                                <span style={{ color: isOverdue ? '#28a745' : '#445566', fontWeight: isOverdue ? 600 : 400, fontSize: '13px' }}>
                                                    {isOverdue ? 'Ready to send' : nextDate.toLocaleDateString()}
                                                </span>
                                            ) : 'N/A'}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </>
            )}

            {/* Recent Activity Feed */}
            {status.recentActivity?.length > 0 && (
                <>
                    <h3 style={sectionHeaderStyle}>
                        <Icon name="history" style={{ color: '#0884a8' }} /> Recent Email Activity
                    </h3>
                    <Segment style={{ maxHeight: '350px', overflowY: 'auto', padding: 0 }}>
                        <Table basic="very" compact unstackable>
                            <Table.Body>
                                {status.recentActivity.map((item, i) => (
                                    <Table.Row key={i}>
                                        <Table.Cell style={{ width: '40px' }}>
                                            <Icon
                                                name={item.type === 'drip' ? 'mail' : 'bell'}
                                                color={item.type === 'drip' ? 'blue' : 'orange'}
                                            />
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Label size="mini" color={item.type === 'drip' ? 'blue' : 'orange'}>
                                                {item.type === 'drip' ? `Drip Step ${item.step}` : 'Weekly'}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell style={{ fontWeight: 500 }}>
                                            {item.firstName} {item.lastName}
                                        </Table.Cell>
                                        <Table.Cell style={{ color: '#445566', fontSize: '13px' }}>
                                            {item.email}
                                        </Table.Cell>
                                        <Table.Cell style={{ color: '#888', fontSize: '13px', textAlign: 'right' }}>
                                            {item.sentAt ? formatTimeAgo(new Date(item.sentAt)) : 'Unknown'}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </Segment>
                </>
            )}

            <div style={{ marginTop: '16px', textAlign: 'right' }}>
                <Button icon="refresh" content="Refresh" onClick={fetchStatus} basic color="blue" />
            </div>

            {/* Toggle Confirmation */}
            <Confirm
                open={toggleConfirmOpen}
                onCancel={() => setToggleConfirmOpen(false)}
                onConfirm={handleToggle}
                header={status.effectivelyEnabled ? 'Disable Email Jobs?' : 'Enable Email Jobs?'}
                content={status.effectivelyEnabled
                    ? 'This will stop all automated drip and weekly reminder emails from being sent. You can re-enable them at any time.'
                    : 'This will enable automated drip and weekly reminder emails. Emails will begin sending on their next scheduled run.'
                }
                confirmButton={{
                    content: status.effectivelyEnabled ? 'Disable' : 'Enable',
                    loading: toggling,
                    negative: status.effectivelyEnabled,
                    positive: !status.effectivelyEnabled,
                }}
            />
        </div>
    );
};

function formatTimeAgo(date) {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
}

export default EmailOverview;
